import {isTruthy} from "@khanacademy/wonder-stuff-core";
import {RequestInfo} from "@khan/shared-core";

// sync-start:dev-domains 8200511 https://github.com/Khan/webapp/blob/master/dev/server/Makefile
const _DEV_DOMAINS = ["localhost", "khanacademy.dev"] as const;
// sync-end:dev-domains

/**
 * HostnameParts is a class for parsing and storing different parts of
 * a URL that are important to us when computing client-side redirects
 * such as `lang`, `baseDomain`, etc.  For the complete list of parts and
 * more details on each part refer to the comments on the instance member
 * declarations at the top of the class definition.
 *
 * An instances of HostnameParts is usually constructed using the static
 * `parse` method, like so:
 *
 * `const p = HostnameParts.parse("https://es.partitioned.khanacademy.org/");`
 *
 * While this class is roughly equivalent to HostnameParts defined in
 * intl/url_util.py, there is one substantial difference.  There is no
 * `replace` method.  Instead there's `setLang` since that's the only part
 * we need to update for redirects on the client.
 */
export class HostnameParts {
    // The base domain name, e.g. khanacademy.org in prod or
    // localhost or khanacademy.dev in dev.  May be null, if we don't
    // support language subdomains on this domain, such as appspot.com, or
    // False, if the given domain name wasn't something we could parse.
    // (Many callers don't care about the distinction, but a few do.)
    // In this case all other attributes will be null/False as well.
    baseDomain: string | null | undefined;
    // If this request's subdomain had extra garbage in it
    // that we couldn't parse, set to those extra parts.  If set, the
    // request likely won't make it past I18nMiddleware, which will
    // redirect it to a more reasonable place, but a few callers within
    // that middleware want this data, so we set it.
    extraParts: string | null | undefined;
    // True if this request went to partitioned.khanacademy.org. The
    // partitioned subdomain uses partitioned cookies (instead of
    // SameSite=Lax) does not have language variants (the ?lang=XX query
    // parameter is always used).
    partitioned: boolean;
    // The language subdomain from the given domain.  Note this is not
    // necessarily the ka_locale; for example it may be 'www'.  null if
    // the request domain did not specify a language.
    lang: string | null | undefined;
    // The fallback language from the given domain: 'accents', 'boxes', or null.
    langFallback: string | null | undefined;
    // The commit sha from the given domain, or None.
    publishCommit: string | null | undefined;
    // The version-string from the given domain, or None
    servicesVersion: string | null | undefined;
    // True if the domain requested a preview FMS
    usePreviewFMS: boolean;

    constructor(
        baseDomain: string | null | undefined,
        extraParts: string | null | undefined,
        partitioned: boolean,
        lang: string | null | undefined,
        langFallback: string | null | undefined,
        publishCommit: string | null | undefined,
        servicesVersion: string | null | undefined,
        usePreviewFMS: boolean,
    ) {
        this.baseDomain = baseDomain;
        this.extraParts = extraParts;
        this.partitioned = partitioned;
        this.lang = lang;
        this.langFallback = langFallback;
        this.publishCommit = publishCommit;
        this.servicesVersion = servicesVersion;
        this.usePreviewFMS = usePreviewFMS;
    }

    /**
     * Get a full domain name equivalent to this one.
     *
     * Note that this is not guaranteed to be the input to parse(), it's just
     * equivalent.
     *
     * Returns null if the language (or similar metadata) can't be encoded in
     * the domain, such as if
     *     this.baseDomain is null (see class docstring), or if
     *     this.baseDomain is localhost but we also have a language.
     *     this.partitioned is true but we also have a language.
     *     TODO(benkraft): Today most browsers seem to handle *.localhost just
     *         fine; use that instead of ?lang on dev.
     *
     * When null, callers should convey the information via other means, e.g.
     * via the ?lang= query parameter.
     */
    domain(): null | string {
        if (!this.baseDomain) {
            return null;
        }

        let langPart = null;
        if (this.lang) {
            if (this.langFallback) {
                // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null'.
                langPart = `${this.lang}-${this.langFallback}`;
            } else if (this.lang !== "www") {
                // We omit the language tag if it's www.
                // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null'.
                langPart = this.lang;
            }
        }

        let subdomain = [
            this.publishCommit ? `pubcommit-${this.publishCommit}` : null,
            this.servicesVersion ? `prod-${this.servicesVersion}` : null,
            this.usePreviewFMS ? "preview" : null,
            langPart,
        ]
            .filter(isTruthy)
            .join("--");

        if (this.baseDomain === "localhost") {
            if (subdomain) {
                return null;
            }
            return this.baseDomain;
        }

        if (this.partitioned) {
            if (subdomain) {
                return null;
            }
            return "partitioned." + this.baseDomain;
        }

        if (!subdomain) {
            subdomain = "www";
        }

        return [subdomain, this.baseDomain].filter(isTruthy).join(".");
    }

    setLang(languageTag: string) {
        const [lang, langFallback] = HostnameParts.splitLang(languageTag);
        this.lang = lang;
        this.langFallback = langFallback;
    }

    /**
     * Return the host's base domain.
     *
     * This is useful for setting cookies that can be shared by all our
     * subdomains.
     *
     * Returns: either 'localhost' or 'khanacademy.(org|dev|local|test)',
     *     or None if the domain is none of those.
     *
     * TODO(marksandstrom): There are no more callers. Remove this.
     */
    static getBaseDomain(host: string): string | null | undefined {
        const hostname = host.split(":")[0];
        return HostnameParts.parse(hostname).baseDomain;
    }

    /**
     * Return the full subdomain component of the host.
     *
     * Returns: a string like 'www' for 'www.khanacademy.org' or None if
     * get_base_domain(host) is None.
     *
     * TODO(marksandstrom): There are no more callers. Remove this.
     */
    static getSubdomain(host: string): null | string {
        const hostname = host.split(":")[0];
        const base = HostnameParts.parse(hostname).baseDomain;

        if (base && hostname.endsWith(base)) {
            // remove the base domain and the dot.
            return hostname.slice(0, -(base.length + 1));
        }
        return null;
    }

    /**
     * Given a hostname, return a HostnameParts instance representing it.
     */
    static parse(hostname: string): HostnameParts {
        const _VALID_BASE_DOMAINS = RequestInfo.IS_DEV_SERVER
            ? ["khanacademy.org", ..._DEV_DOMAINS]
            : ["khanacademy.org"];

        for (const baseDomain of _VALID_BASE_DOMAINS) {
            if (hostname === baseDomain) {
                return HostnameParts.fromParts(baseDomain);
            }
            if (hostname.endsWith(`.${baseDomain}`)) {
                const subdomain = hostname.slice(0, -(baseDomain.length + 1));
                return HostnameParts.fromParts(baseDomain, subdomain);
            }
        }
        return HostnameParts.fromParts();
    }

    static fromParts(baseDomain?: string, subdomain?: string): HostnameParts {
        let extraParts = null;
        let partitioned = false;
        let lang = null;
        let langFallback = null;
        let publishCommit = null;
        let servicesVersion = null;
        let usePreviewFMS = false;

        if (subdomain) {
            // sync-start:language-subdomain 712698045 https://github.com/Khan/webapp/blob/master/services/fastly-khanacademy-compute/request_data/hostname_parts.go
            let subdomainParts = subdomain.split(".");

            partitioned = subdomainParts.includes("partitioned");
            if (partitioned) {
                subdomainParts = remove(subdomainParts, "partitioned");
            }

            if (subdomainParts.length > 0) {
                const firstSubdomain = subdomainParts[0];
                // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null'.
                extraParts = subdomainParts.slice(1).join(".");

                for (const part of firstSubdomain.split("--")) {
                    if (part.startsWith("pubcommit-")) {
                        // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null'.
                        publishCommit = part.slice("pubcommit-".length);
                    } else if (part.startsWith("prod-")) {
                        // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null'.
                        servicesVersion = part.slice("prod-".length);
                    } else if (part.startsWith("static-")) {
                        // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null'.
                        servicesVersion = part.slice("static-".length);
                    } else if (part === "preview") {
                        usePreviewFMS = true;
                    } else {
                        // @ts-expect-error - TS2322 - Type 'string | null | undefined' is not assignable to type 'null'. | TS2322 - Type 'string | null | undefined' is not assignable to type 'null'.
                        [lang, langFallback] = HostnameParts.splitLang(part);
                    }
                }
            }
            // sync-end:language-subdomain
        }

        return new HostnameParts(
            baseDomain,
            extraParts,
            partitioned,
            lang,
            langFallback,
            publishCommit,
            servicesVersion,
            usePreviewFMS,
        );
    }

    static splitLang(lang: string): Array<string | null | undefined> {
        // sync-start:split-lang 1087948873 https://github.com/Khan/webapp/blob/master/services/fastly-khanacademy-compute/request_data/hostname_parts.go
        const match = lang.match(/(.*)-(accents|boxes)/);
        if (match) {
            return [match[1], match[2]];
        }
        return [lang, null];
        // sync-end:split-lang
    }
}

const remove = <T>(array: Array<T>, item: T): Array<T> => {
    const index = array.indexOf(item);
    if (index !== -1) {
        return [...array.slice(0, index), ...array.slice(index + 1)];
    }
    return array;
};
